import { Button, Form, Input, Flex, Select } from "antd";
import { FORM_REQUIRED } from "../../../configs/configs";
import { useEffect, useState, useContext } from "react";
import useFetch from "../../../hooks/useFetch";
import { IndexContext } from "../../../context/IndexContext";

const { TextArea } = Input;

function EmissionSourceForm({
  mode,
  setIsEmissionSourceFormModalOpen,
  refreshTable,
  initialValues,
}) {
  const [fetchWrapper] = useFetch();

  // selection for scope
  const [scopeSelected, setScopeSelected] = useState(null);
  const [scopeList, setScopeList] = useState([]);
  const [scopeListLoading, setScopeListLoading] = useState(false);

  // selection for scope type
  const [scopeTypeSelected, setScopeTypeSelected] = useState(null);
  const [scopeTypeList, setScopeTypeList] = useState([]);
  const [scopeTypeListLoading, setScopeTypeListLoading] = useState(false);
  const requiredCategoryTypeScopeTypeList = [6, 7, 8, 9]; // TODO: find a not so hard code way to determine required (maybe can pass the scope type id that required category type from backend)

  // selection for category type
  const [categoryTypeSelected, setCategoryTypeSelected] = useState(null);
  const [categoryTypeList, setCategoryTypeList] = useState([]);
  const [categoryTypeListLoading, setCategoryTypeListLoading] = useState(false);

  // selection for dimension
  const [dimensionSelected, setDimensionSelected] = useState(null);
  const [dimensionList, setDimensionList] = useState([]);
  const [dimensionListLoading, setDimensionListLoading] = useState(false);

  // loading states
  const [loadingSubmitButton, setLoadingSubmitButton] = useState(false);
  const loading =
    scopeListLoading ||
    scopeTypeListLoading ||
    categoryTypeListLoading ||
    dimensionListLoading;

  const [emissionSourceForm] = Form.useForm();

  // load index context
  const { messageApi, authenticationState } = useContext(IndexContext);

  /* API to get scope */
  function getScope() {
    setScopeListLoading(true);
    fetchWrapper({
      endpoint_url: "emission_source_management/getScopeList",
      onSuccess: (response) => {
        setScopeList(response.data);
        setScopeListLoading(false);
      },
    });
  }

  /* API to get dimension */
  function getDimension() {
    setDimensionListLoading(true);
    fetchWrapper({
      endpoint_url: "emission_source_management/getDimensionList",
      onSuccess: (response) => {
        setDimensionList(response.data);
        setDimensionListLoading(false);
      },
    });
  }

  /* API to get scope type */
  function getScopeType() {
    setScopeTypeListLoading(true);

    fetchWrapper({
      endpoint_url: "emission_source_management/getScopeTypeList",
      body: {
        scope_id: scopeSelected,
      },
      onSuccess: (response) => {
        setScopeTypeList(response.data);
        setScopeTypeListLoading(false);
      },
    });
  }

  /* API to get category type */
  function getCategoryType() {
    setCategoryTypeListLoading(true);

    fetchWrapper({
      endpoint_url: "emission_source_management/getCategoryTypeList",
      body: {
        scope_type_id: scopeTypeSelected,
      },
      onSuccess: (response) => {
        setCategoryTypeList(response.data);
        setCategoryTypeListLoading(false);
      },
    });
  }

  /* API to add emission source */
  function addEmissionSource(user_inputs) {
    setLoadingSubmitButton(true);
    fetchWrapper({
      endpoint_url: "emission_source_management/addEmissionSource",
      body: {
        scope_id: user_inputs.ScopeID,
        scope_type_id: user_inputs.ScopeTypeID,
        category_type_id: user_inputs.CategoryTypeID
          ? user_inputs.CategoryTypeID
          : null,
        dimension_id: user_inputs.DimensionID,
        emission_source: user_inputs.EmissionSource,
        remark: user_inputs.Remark ? user_inputs.Remark : null,
      },
      onSuccess: (response) => {
        if (response.data.status) {
          messageApi.open({
            type: "success",
            content: response.message,
          });
        } else {
          messageApi.open({
            type: "error",
            content: response.message,
          });
        }
        setIsEmissionSourceFormModalOpen(false);
        emissionSourceForm.resetFields();
        setLoadingSubmitButton(false);
        refreshTable();
      },
    });
  }

  /* API to edit user group or user and their site access */
  function editEmissionSource(user_inputs) {
    setLoadingSubmitButton(true);
    fetchWrapper({
      endpoint_url: "emission_source_management/editEmissionSource",
      body: {
        emission_source_id: initialValues[0].ID,
        emission_source: user_inputs.EmissionSource,
        dimension_id: user_inputs.DimensionID,
        remark: user_inputs.Remark ? user_inputs.Remark : null, // admin can't select user group, so need to pass null to backend
      },
      onSuccess: (response) => {
        if (response.data.status === true) {
          messageApi.open({
            type: "success",
            content: response.message,
          });
          setIsEmissionSourceFormModalOpen(false);
          emissionSourceForm.resetFields();
        } else {
          messageApi.open({
            type: "error",
            content: response.message,
          });
        }
        setLoadingSubmitButton(false);
        refreshTable();
      },
    });
  }

  useEffect(() => {
    getScope();
    getDimension();
  }, []);

  useEffect(() => {
    if (mode === "add") {
      setScopeTypeSelected(null);
      emissionSourceForm.setFieldValue("ScopeTypeID", null);
    }
    if (scopeSelected) {
      getScopeType();
    }
  }, [scopeSelected]);

  useEffect(() => {
    if (mode === "add") {
      setCategoryTypeSelected(null);
      emissionSourceForm.setFieldValue("CategoryTypeID", null);
    }
    if (scopeTypeSelected) {
      getCategoryType();
    }
  }, [scopeSelected, scopeTypeSelected]);

  useEffect(() => {
    if (mode === "edit") {
      if (initialValues.length) {
        emissionSourceForm.setFieldsValue(initialValues[0]);
        setScopeSelected(initialValues[0]?.ScopeID ?? null);
        setScopeTypeSelected(initialValues[0]?.ScopeTypeID ?? null);
        setCategoryTypeSelected(initialValues[0]?.CategoryTypeID ?? null);
      }
    }
  }, [mode, initialValues]);

  return (
    <>
      <Form
        form={emissionSourceForm}
        name="emission-source-form"
        labelCol={{
          span: 8,
        }}
        wrapperCol={{
          span: 16,
        }}
        onFinish={(values) => {
          if (mode === "add") {
            addEmissionSource(values);
          } else {
            editEmissionSource(values);
          }
        }}
        autoComplete="off"
      >
        <Form.Item
          label="Scope"
          name="ScopeID"
          rules={[
            {
              required: FORM_REQUIRED,
              message: "You must select scope.",
            },
          ]}
        >
          <Select
            placeholder="Select scope"
            loading={scopeListLoading}
            disabled={mode === "edit"}
            value={scopeSelected}
            options={scopeList}
            onChange={(value) => setScopeSelected(value)}
          />
        </Form.Item>

        <Form.Item
          label="Scope Type"
          name="ScopeTypeID"
          rules={[
            {
              required: FORM_REQUIRED,
              message: "You must select scope type.",
            },
          ]}
        >
          <Select
            placeholder="Select scope type"
            loading={scopeTypeListLoading}
            disabled={!scopeSelected || mode === "edit"}
            value={scopeTypeSelected}
            options={scopeTypeList}
            onChange={(value) => setScopeTypeSelected(value)}
          />
        </Form.Item>

        {categoryTypeList.length !== 0 && (
          <Form.Item
            label="Category Type"
            name="CategoryTypeID"
            rules={[
              {
                required:
                  requiredCategoryTypeScopeTypeList.includes(scopeTypeSelected),
                message: "You must select category type.",
              },
            ]}
          >
            <Select
              placeholder="Select category type"
              loading={categoryTypeListLoading}
              disabled={
                !scopeTypeSelected ||
                categoryTypeList.length === 0 ||
                mode === "edit"
              }
              value={categoryTypeSelected}
              options={categoryTypeList}
              onChange={(value) => setCategoryTypeSelected(value)}
            />
          </Form.Item>
        )}

        <Form.Item
          label="Emission Source"
          name="EmissionSource"
          rules={[
            {
              required: FORM_REQUIRED,
              message: "Emission source cannot be empty.",
            },
          ]}
        >
          <Input placeholder="Input emission source" />
        </Form.Item>

        <Form.Item
          label="Dimension"
          name="DimensionID"
          rules={[
            {
              required: FORM_REQUIRED,
              message: "You must select dimension.",
            },
          ]}
        >
          <Select
            placeholder="Select dimension"
            loading={dimensionListLoading}
            value={dimensionSelected}
            options={dimensionList}
            onChange={(value) => setDimensionSelected(value)}
          />
        </Form.Item>

        <Form.Item label="Remark" name="Remark">
          <TextArea
            rows={4}
            placeholder="Input remark (if any)"
            maxLength={100}
            showCount
          />
        </Form.Item>

        <Flex justify="center">
          <Button
            className="mr-2"
            onClick={() => {
              setIsEmissionSourceFormModalOpen(false);
              emissionSourceForm.resetFields();
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            disabled={loading}
            loading={loadingSubmitButton}
          >
            Submit
          </Button>
        </Flex>
      </Form>
    </>
  );
}

export default EmissionSourceForm;
